import React from 'react';
import {Pressable, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import type {Challenge} from '@/Hooks/ClubsApi/types';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

const AVATAR_COLORS = ['#3DAB9E', '#E17846', '#67B3E1', '#937ADB', '#D1AB41'];
const avatarColor = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
  }
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initials = (s: string) => (s || '?').slice(0, 2).toUpperCase();

// A game challenge card in the club chat (design lines 376–390).
export default function ChallengeCard({
  challenge,
  onAccept,
  onPlay,
  onCancel,
  onReject,
  mine = false,
}: {
  challenge: Challenge;
  onAccept: () => void;
  onPlay: () => void;
  onCancel?: () => void;
  onReject?: () => void;
  mine?: boolean;
}) {
  const {isMobile} = useMediaQuery();
  const isWide = !isMobile;
  const creator = challenge.created_by || 'Someone';
  const opponent = challenge.opponent || 'Opponent';
  const hasPairing =
    challenge.state === 'accepted' || challenge.state === 'done';
  const winner =
    challenge.result === 'win'
      ? creator
      : challenge.result === 'loss'
      ? opponent
      : null;
  const loser =
    challenge.result === 'win'
      ? opponent
      : challenge.result === 'loss'
      ? creator
      : null;
  const resultRows =
    challenge.result === 'draw'
      ? [
          {name: creator, status: 'DRAW', badge: '#D1AB41', trophy: false},
          {name: opponent, status: 'DRAW', badge: '#D1AB41', trophy: false},
        ]
      : winner && loser
      ? [
          {name: winner, status: 'WON', badge: '#1E7A3D', trophy: true},
          {name: loser, status: 'LOST', badge: '#CD2C39', trophy: false},
        ]
      : [];

  return (
    <View
      testID={`challenge-card-${challenge.id}`}
      style={{
        // Align like a chat message: right if I created it, left if someone else did.
        alignSelf: mine ? 'flex-end' : 'flex-start',
        width: isWide ? '50%' : '90%',
        backgroundColor: '#fff',
        borderRadius: 14,
        padding: 14,
        marginBottom: 11,
        borderWidth: 1,
        borderColor: 'rgba(38,35,34,0.10)',
        shadowColor: '#4D3F37',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.05,
        shadowRadius: 2,
      }}>
      <View style={{flexDirection: 'row', alignItems: 'center', gap: 10}}>
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: avatarColor(creator),
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <CCText style={{color: '#fff', fontWeight: '600', fontSize: 13}}>
            {initials(creator)}
          </CCText>
        </View>
        <View style={{flex: 1}}>
          <CCText style={{fontSize: 14, fontWeight: '600', color: '#262322'}}>
            {hasPairing
              ? `♞ ${creator} vs ${opponent}`
              : `♞ ${creator} wants a game`}
          </CCText>
          <CCText style={{fontSize: 12, color: '#888280'}}>
            {challenge.time_control} · {challenge.rated ? 'Rated' : 'Casual'}
            {challenge.state === 'done' ? ' · Completed' : ''}
          </CCText>
        </View>
      </View>

      {challenge.state === 'open' ? (
        // Others see Accept; the creator sees a Cancel affordance instead.
        challenge.is_mine ? (
          onCancel ? (
            <Pressable
              testID={`challenge-cancel-${challenge.id}`}
              onPress={onCancel}
              style={{marginTop: 11, alignItems: 'center', paddingVertical: 8}}>
              <CCText
                style={{color: '#888280', fontWeight: '600', fontSize: 13}}>
                Cancel challenge
              </CCText>
            </Pressable>
          ) : null
        ) : (
          <View style={{marginTop: 11, flexDirection: 'row', gap: 8}}>
            <Pressable
              testID={`challenge-accept-${challenge.id}`}
              onPress={onAccept}
              style={{
                flex: 1,
                backgroundColor: '#3DAB9E',
                borderRadius: 10,
                paddingVertical: 11,
                alignItems: 'center',
              }}>
              <CCText style={{color: '#fff', fontWeight: '600', fontSize: 14}}>
                Accept
              </CCText>
            </Pressable>
            {onReject ? (
              <Pressable
                testID={`challenge-reject-${challenge.id}`}
                onPress={onReject}
                style={{
                  flex: 1,
                  backgroundColor: '#FDECEE',
                  borderRadius: 10,
                  paddingVertical: 11,
                  alignItems: 'center',
                }}>
                <CCText
                  style={{color: '#CD2C39', fontWeight: '600', fontSize: 14}}>
                  Reject
                </CCText>
              </Pressable>
            ) : null}
          </View>
        )
      ) : null}

      {challenge.state === 'accepted' ? (
        <View
          style={{
            marginTop: 11,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#F0FAF8',
            borderRadius: 10,
            paddingVertical: 10,
            paddingHorizontal: 12,
          }}>
          <CCText style={{fontSize: 13, fontWeight: '600', color: '#2C7B70'}}>
            {challenge.is_mine
              ? `You vs ${challenge.opponent || 'opponent'} · ${
                  challenge.time_control
                }`
              : `You vs ${creator} · ${challenge.time_control}`}
          </CCText>
          {/* Only the two participants get the Play button. */}
          {challenge.is_mine || challenge.am_opponent ? (
            <Pressable
              testID={`challenge-play-${challenge.id}`}
              onPress={onPlay}
              style={{
                backgroundColor: '#FACF47',
                borderRadius: 8,
                paddingVertical: 6,
                paddingHorizontal: 12,
              }}>
              <CCText
                style={{color: '#4D3F37', fontWeight: '600', fontSize: 12.5}}>
                Play now
              </CCText>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {challenge.state === 'done' ? (
        <View
          testID={`challenge-result-${challenge.id}`}
          style={{
            marginTop: 11,
            backgroundColor:
              challenge.result === 'win'
                ? '#E4F6EB'
                : challenge.result === 'loss'
                ? '#FDECEE'
                : '#FFF8E1',
            borderRadius: 10,
            paddingVertical: 10,
            paddingHorizontal: 12,
          }}>
          {resultRows.length ? (
            resultRows.map((player, index) => (
              <View
                key={`${player.name}-${player.status}`}
                testID={`challenge-result-player-${index}`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: 5,
                  borderBottomWidth: index === resultRows.length - 1 ? 0 : 1,
                  borderBottomColor: 'rgba(38,35,34,0.08)',
                }}>
                <CCText
                  numberOfLines={1}
                  style={{
                    flex: 1,
                    marginRight: 10,
                    fontSize: 13,
                    fontWeight: player.trophy ? '700' : '600',
                    color: '#262322',
                  }}>
                  {player.trophy ? '🏆 ' : challenge.result === 'draw' ? '🤝 ' : ''}
                  {player.name}
                </CCText>
                <View
                  style={{
                    minWidth: 52,
                    paddingVertical: 3,
                    paddingHorizontal: 8,
                    borderRadius: 999,
                    backgroundColor: player.badge,
                    alignItems: 'center',
                  }}>
                  <CCText
                    style={{fontSize: 10, fontWeight: '700', color: '#fff'}}>
                    {player.status}
                  </CCText>
                </View>
              </View>
            ))
          ) : (
            <CCText
              style={{
                fontSize: 13,
                fontWeight: '700',
                color: '#806234',
                textAlign: 'center',
              }}>
              Result unavailable
            </CCText>
          )}
        </View>
      ) : null}
    </View>
  );
}
